import {
  orderBy as _orderBy,
  take as _take,
  minBy as _minBy,
  map as _map,
  includes as _includes,
  find as _find,
  isObject as _isObject
} from 'lodash';
import { track } from '@/utils/heap';

const reward = 20;

const state = {
  finalists: [] // which players are playing the finale?
};

const actions = {
  startRound: ({ commit, getters }) => {
    commit('SEND_PLAYERS_TO_FINALE', getters.playersToGoToFinale);
    commit('ACTIVATE_PLAYER', getters.nextPlayerToStart.id, { root: true });
    commit('SET_CURRENT_QUESTION', 0, { root: true });
    commit('SHOW_PLAYER_TURN_SCREEN', null, { root: true });
  },
  startQuestion: ({ commit, dispatch, rootGetters }) => {
    return dispatch('startClockForActivePlayer', null, { root: true }).then(
      () => {
        commit('END_PLAYER_FIRST_TURN', rootGetters.activePlayer.id, {
          root: true
        });
        commit('SHOW_ROUND_PLAYSCREEN', null, { root: true });
      }
    );
  },
  answerQuestion: ({ commit, getters, dispatch, rootGetters }, keywordId) => {
    const playerId = rootGetters.activePlayer.id;
    commit('MARK_KEYWORD_AS_FOUND', { keywordId, playerId }, { root: true });
    // game over when the opponent goes below zero
    if (getters.opponent.score - reward <= 0) {
      dispatch('gameOver', {
        winnerId: playerId,
        loserId: getters.opponent.id
      });
    } else {
      commit(
        'REWARD_PLAYER',
        { playerId: getters.opponent.id, score: -reward },
        { root: true }
      );
      if (getters.allKeywordsAreFound) {
        dispatch('stopClock');
      }
    }
  },
  nextQuestion: ({ rootState, commit, getters }) => {
    // set current question to 0 if out of questions
    if (rootState.game.currentQuestion + 1 < getters.questions.length) {
      commit('NEXT_QUESTION', null, { root: true });
    } else {
      commit('SET_CURRENT_QUESTION', 0, { root: true });
    }
    commit('DISABLE_REVIEW_MODE', null, { root: true });
    commit('RESET_ALL_PLAYERS_TURNS', null, { root: true });
    commit('RESET_FOUND_KEYWORDS', null, { root: true });
    // next lowest player in finale
    commit('ACTIVATE_PLAYER', getters.nextPlayerToStart.id, { root: true });
    commit('SHOW_PLAYER_TURN_SCREEN', null, { root: true });
  },
  stopClock: ({ dispatch, commit, getters, rootGetters }) => {
    commit('STOP_CLOCK', null, { root: true });
    dispatch('endActivePlayerTurn', null, { root: true });
    if (
      !getters.allKeywordsAreFound &&
      getters.nextPlayer &&
      !rootGetters.gameEnded
    ) {
      commit('ACTIVATE_PLAYER', getters.nextPlayer.id, { root: true });
    } else {
      dispatch('enableReviewMode', null, { root: true });
    }
  },
  gameOver: ({ rootState, commit, dispatch }, { winnerId, loserId }) => {
    commit('SET_WINNER', winnerId, { root: true });
    commit('SHOW_GAME_OVER_SCREEN', null, { root: true });
    commit(
      'CHANGE_SCORE_OF_PLAYER',
      { playerId: loserId, score: 0 },
      { root: true }
    );
    dispatch('stopClock');
    // send to analytics
    track('View Page: "Eindscherm"', { gameId: rootState.originalGameId });
  }
};

const mutations = {
  SEND_PLAYERS_TO_FINALE: (state, players) => {
    state.finalists = _map(players, 'id');
  }
};

const getters = {
  questions(state, getters, rootState) {
    return rootState.questionsPerRound[5].questions;
  },
  activeQuestion(state, getters, rootState) {
    return getters.questions[rootState.game.currentQuestion];
  },
  // the players who should go the finale (not yet saved to state)
  playersToGoToFinale(state, getters, rootState, rootGetters) {
    const sortOrder = rootState.bestPlayersInFinale ? 'desc' : 'asc';
    return _take(_orderBy(rootGetters.players, ['score'], [sortOrder]), 2);
  },
  // the players who are in the finale (saved to the state)
  playersInFinale(state, getters, rootState, rootGetters) {
    return rootGetters.players.filter(player => {
      return _includes(state.finalists, player.id);
    });
  },
  // the players who are in the finale (saved to the state)
  inactivePlayersInFinale(state, getters, rootState, rootGetters) {
    if (rootGetters.activePlayer) {
      return getters.playersInFinale.filter(
        player => player.id !== rootGetters.activePlayer.id
      );
    } else {
      return getters.playersInFinale;
    }
  },
  playersWithOpenTurns(state, getters) {
    return getters.playersInFinale.filter(player => !player.tookTurn);
  },
  nextPlayer(state, getters) {
    return _minBy(getters.playersWithOpenTurns, 'score');
  },
  opponent(state, getters, rootState, rootGetters) {
    if (rootGetters.activePlayer) {
      return getters.playersInFinale.find(
        player => player.id !== rootGetters.activePlayer.id
      );
    } else {
      return null;
    }
  },
  nextPlayerToStart(state, getters) {
    return _minBy(getters.playersInFinale, 'score');
  },
  activeKeywords(state, getters, rootState, rootGetters) {
    return getters.activeQuestion.keywords.map((keyword, keywordId) => {
      const foundKeyword = _find(
        rootState.game.foundKeywords,
        k => k.keywordId === keywordId
      );
      return {
        id: keywordId,
        name: keyword,
        found: _isObject(foundKeyword),
        foundBy: foundKeyword
          ? rootGetters.players[foundKeyword.playerId].name
          : false,
        score: reward
      };
    });
  },
  allKeywordsAreFound(state, getters, rootState) {
    return (
      rootState.game.foundKeywords.length ===
      getters.activeQuestion.keywords.length
    );
  }
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
